import { useEffect, useRef, useState } from 'react';
import { supabaseClient } from '../../config/supabase';

export default function VerificationCodeSettings({ orgId }) {
    const [channel, setChannel] = useState('email');
    const [smsEnabled, setSmsEnabled] = useState(false);
    const [automaticDelivery, setAutomaticDelivery] = useState(false);
    const [inviteExpiryHours, setInviteExpiryHours] = useState('72');
    const [resetAlertThreshold, setResetAlertThreshold] = useState('3');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [loaded, setLoaded] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const fieldRefs = useRef({});

    useEffect(() => {
        let active = true;
        setLoading(true); setLoaded(false); setError('');
        supabaseClient.functions.invoke('auth-verification', { body: { action: 'settings' } })
            .then(({ data, error: requestError }) => {
                if (!active) return;
                if (requestError || !data?.ok) throw new Error(data?.error || requestError?.message || 'Could not load code settings.');
                setChannel(data.channel); setSmsEnabled(data.smsEnabled);
                setAutomaticDelivery(data.deliveryPolicy === 'sms_if_available');
                setInviteExpiryHours(String(data.inviteExpiryHours ?? 72));
                setResetAlertThreshold(String(data.resetAlertThreshold ?? 3));
                setFieldErrors({});
                setLoaded(true);
            }).catch((e) => { if (active) setError(e.message); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [orgId]);

    function validateField(field, rawValue) {
        const value = Number(rawValue);
        if (field === 'inviteExpiryHours' && (!Number.isInteger(value) || value < 1 || value > 168)) {
            return 'Enter a whole number from 1 to 168 hours.';
        }
        if (field === 'resetAlertThreshold' && (!Number.isInteger(value) || value < 1 || value > 20)) {
            return 'Enter a whole number from 1 to 20 requests.';
        }
        return '';
    }

    function updateField(field, value) {
        if (field === 'inviteExpiryHours') setInviteExpiryHours(value);
        else setResetAlertThreshold(value);
        setFieldErrors((current) => ({
            ...current,
            [field]: validateField(field, value),
        }));
        setError('');
        setMessage('');
    }

    async function save(value = channel) {
        setError(''); setMessage('');
        const nextErrors = {
            inviteExpiryHours: validateField('inviteExpiryHours', inviteExpiryHours),
            resetAlertThreshold: validateField('resetAlertThreshold', resetAlertThreshold),
        };
        setFieldErrors(nextErrors);
        const firstInvalid = Object.keys(nextErrors).find((field) => nextErrors[field]);
        if (firstInvalid) {
            fieldRefs.current[firstInvalid]?.focus();
            return;
        }
        setLoading(true);
        try {
            const expiryHours = Number(inviteExpiryHours);
            const alertThreshold = Number(resetAlertThreshold);
            const { data, error: requestError } = await supabaseClient.functions.invoke('auth-verification', {
                body: {
                    action: 'save_settings', channel: value,
                    inviteExpiryHours: expiryHours, resetAlertThreshold: alertThreshold,
                },
            });
            if (requestError || !data?.ok) throw new Error(data?.error || requestError?.message || 'Could not save code settings.');
            setChannel(data.channel);
            setInviteExpiryHours(String(data.inviteExpiryHours));
            setResetAlertThreshold(String(data.resetAlertThreshold));
            setFieldErrors({});
            setMessage('Verification settings saved.');
        } catch (e) { setError(e.message); }
        finally { setLoading(false); }
    }

    return <section className="space-y-3 p-6 text-[var(--ink)]">
        <h2 className="text-lg font-semibold">Invite and password verification</h2>
        {automaticDelivery ? <p>Invite and password-reset codes go by text only. If a person has no usable mobile number,
            or the text fails, no email is sent and they are told to ask their admin to add or check their number.</p> : <label className="block">Send codes by:
            <select aria-label="Send codes by" disabled={loading || !loaded} value={channel}
                onChange={(event) => save(event.target.value)} className="ml-3 rounded border p-2 bg-[var(--surface-sunken)]">
                <option value="email">Email</option><option value="sms" disabled={!smsEnabled}>SMS</option>
                <option value="both" disabled={!smsEnabled}>Both</option>
            </select>
        </label>}
        <div>
            <label className="block" htmlFor="invite-expiry-hours">Invitation link expires after:
                <input id="invite-expiry-hours" ref={(element) => { fieldRefs.current.inviteExpiryHours = element; }}
                    aria-label="Invitation link expiry hours" aria-invalid={Boolean(fieldErrors.inviteExpiryHours)}
                    aria-describedby={fieldErrors.inviteExpiryHours ? 'invite-expiry-hours-error' : undefined}
                    type="number" min="1" max="168" step="1"
                    disabled={loading || !loaded} value={inviteExpiryHours}
                    onChange={(event) => updateField('inviteExpiryHours', event.target.value)}
                    onBlur={(event) => updateField('inviteExpiryHours', event.target.value)}
                    className={`ml-3 w-24 rounded border p-2 bg-[var(--surface-sunken)]${fieldErrors.inviteExpiryHours ? ' border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`} />
                <span className="ml-2">hours (1-168)</span>
            </label>
            {fieldErrors.inviteExpiryHours && <p id="invite-expiry-hours-error" role="alert"
                className="mt-1 text-sm text-red-700">{fieldErrors.inviteExpiryHours}</p>}
        </div>
        <div>
            <label className="block" htmlFor="reset-alert-threshold">Password reset alert threshold:
                <input id="reset-alert-threshold" ref={(element) => { fieldRefs.current.resetAlertThreshold = element; }}
                    aria-label="Password reset alert threshold" aria-invalid={Boolean(fieldErrors.resetAlertThreshold)}
                    aria-describedby={fieldErrors.resetAlertThreshold ? 'reset-alert-threshold-error' : undefined}
                    type="number" min="1" max="20" step="1"
                    disabled={loading || !loaded} value={resetAlertThreshold}
                    onChange={(event) => updateField('resetAlertThreshold', event.target.value)}
                    onBlur={(event) => updateField('resetAlertThreshold', event.target.value)}
                    className={`ml-3 w-24 rounded border p-2 bg-[var(--surface-sunken)]${fieldErrors.resetAlertThreshold ? ' border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`} />
                <span className="ml-2">requests (1-20)</span>
            </label>
            {fieldErrors.resetAlertThreshold && <p id="reset-alert-threshold-error" role="alert"
                className="mt-1 text-sm text-red-700">{fieldErrors.resetAlertThreshold}</p>}
        </div>
        <button type="button" disabled={loading || !loaded} onClick={() => save()}
            className="rounded border px-3 py-2 disabled:opacity-50">Save verification settings</button>
        {!smsEnabled && <p>Available after SMS activation</p>}
        {error && <p role="alert">{error}</p>}
        {message && <p role="status">{message}</p>}
    </section>;
}
